import { Box, Heading } from '@byteclaw/visage';
import { GetStaticPaths, GetStaticProps } from 'next';
import { MDXRemote, MDXRemoteSerializeResult } from 'next-mdx-remote';
import { Layout, SEO } from '../../components';
import {
  createSocialImageStaticPageHref,
  createStaticPageHref,
} from '../../helpers/createHref';
import { serializeMdx } from '../../helpers/serializeMdx';
import { mdxComponents } from '../../mdxComponents';
import { graphqlClient, imageLoader } from '../../services';
import {
  AllStaticPagesDocument,
  AllStaticPagesQuery,
  StaticPageDocument,
  StaticPageQuery,
  StaticPageQueryVariables,
} from '../../queries.graphql';

interface StaticPageProps {
  categories: { id: string; name: string; slug: string }[];
  staticPage: {
    publishedAt?: string | null;
    updatedAt?: string | null;
    contentMdx: MDXRemoteSerializeResult;
    slug: string;
    title: string;
  };
}

export default function StaticPage({
  categories,
  staticPage,
}: StaticPageProps) {
  return (
    <Layout categories={categories} maxWidth={680}>
      <SEO
        title={staticPage.title}
        path={createStaticPageHref(staticPage)}
        image={createSocialImageStaticPageHref('fb')}
        meta={[
          <meta key="og-type" property="og:type" content="article" />,
          <meta
            key="og-published-at"
            property="og:published_time"
            // @ts-expect-error this is not nullable because we require published pages
            content={staticPage.publishedAt}
          />,
          <meta
            key="og-modified-at"
            property="og:modified_time"
            // @ts-expect-error this is not nullable because we require published pages
            content={staticPage.updatedAt}
          />,
        ]}
      />
      <Box as="article" styles={{ mb: '2rem' }}>
        <Heading styles={{ fontSize: 4, lineHeight: '1.1em', mb: '1rem' }}>
          {staticPage.title}
        </Heading>
        <MDXRemote {...staticPage.contentMdx} components={mdxComponents} />
      </Box>
    </Layout>
  );
}

export const getStaticProps: GetStaticProps<StaticPageProps, { slug: string }> =
  async function getStaticProps({ params }) {
    const result = await graphqlClient.request<
      StaticPageQuery,
      StaticPageQueryVariables
    >(StaticPageDocument, { slug: params!.slug! });

    if (!result.staticPage) {
      return {
        notFound: true,
      };
    }

    const { content, ...staticPage } = result.staticPage;

    return {
      props: {
        categories: result.categories,
        staticPage: {
          ...staticPage,
          contentMdx: await serializeMdx(content, { imageLoader }),
        },
      },
    };
  };

export const getStaticPaths: GetStaticPaths<{ slug: string }> =
  async function getStaticPaths() {
    const result = await graphqlClient.request<AllStaticPagesQuery>(
      AllStaticPagesDocument,
    );

    return {
      paths: result.staticPages.map((page) => ({
        params: { slug: page.slug },
      })),
      fallback: false,
    };
  };
